import backgroundOverlay from "@/app/(public)/shared/assets/background-overlay2.png";
import { ReactNode } from "react";

interface LandingPageLayoutProps {
  children: ReactNode;
  /** Overrides the default Nowadays-branded purple shell with an event's own background color
   *  (e.g. an attendee dashboard matching its landing page's theme). The purple overlay graphic
   *  is skipped in that case since it assumes the default purple base. */
  backgroundColor?: string | null;
  /**
   * Pins the shell to the viewport instead of letting it grow with its content, for an app-like
   * page that manages its own scrolling (the rebuilt forms builder's three panes).
   *
   * The default `min-h-screen` means the column is at least the viewport but grows taller as
   * content does — so a child with `flex-1 min-h-0 overflow-y-auto` never actually becomes
   * scrollable, because there is no bounded height for it to overflow. `h-dvh` + `overflow-hidden`
   * gives the children a definite height to divide up, which is what makes per-pane scrolling work.
   *
   * Opt-in: every other page keeps the growing shell it has today.
   * @default false
   */
  fullHeight?: boolean;
}

export default function LandingPageLayout({ children, backgroundColor, fullHeight = false }: LandingPageLayoutProps) {
  const heightClasses = fullHeight ? "h-dvh overflow-hidden" : "min-h-screen";

  return (
    <div
      className={`justify-center content-center w-screen flex items-center ${heightClasses} flex-col relative overflow-x-clip ${backgroundColor ? "" : "bg-landing-primary"}`}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {/* Background image with opacity — default shell only */}
      {!backgroundColor && (
        <div
          className="absolute inset-0 pointer-events-none z-0"
          style={{
            backgroundImage: `url(${backgroundOverlay.src})`,
            backgroundSize: "100% auto",
            backgroundPosition: "center top",
            backgroundRepeat: "no-repeat",
            opacity: 0.2,
          }}
        />
      )}

      {/* Content with higher z-index */}
      <div
        className={`relative z-10 w-full flex flex-col items-center ${fullHeight ? "h-full min-h-0" : "min-h-screen"}`}
      >
        {children}
      </div>
    </div>
  );
}
